import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { setIsLoading } from '../general/actions';
import axiosInstance from '../general/axiosInstance';
import { currencyFormater } from '../general/utils';
import { Pager, SOURCE_LABEL, StatusBadge, formatDate, formatDateTime } from '../payments/paymentUtils';

/**
 * Configuración > Pagos. Solo lectura, solo provider_admin (el backend responde 403 al resto).
 */
function Payments({ roles, setIsLoading }) {
    const allowed = (roles || []).includes('provider_admin');

    const [agreements, setAgreements] = useState([]);
    const [reports, setReports] = useState(null);
    const [message, setMessage] = useState('');

    const load = useCallback(async (page = 1) => {
        setIsLoading(true);
        setMessage('');
        try {
            const res = await axiosInstance.get('/payments', { params: { page } });
            setAgreements(res.data.agreements || []);
            setReports(res.data.reports || null);
        } catch (err) {
            setMessage('No se pudo cargar la información de pagos.');
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading]);

    useEffect(() => { if (allowed) load(1); }, [allowed, load]);

    if (!allowed) return <Navigate to="/main" replace />;

    const contractState = (a) => {
        if (!a.has_payables) return <span className="report-status-badge" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>Al día</span>;
        if (a.last_report?.status === 'pending') return <StatusBadge status="pending" />;
        return <span className="report-status-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>Cuentas por pagar</span>;
    };

    return (
        <div style={{ marginTop: 16 }}>
            <h5 style={{ fontWeight: 800 }}>
                <i className="fa fa-credit-card" style={{ color: 'var(--abi-primary)', marginRight: 8 }} />
                Pagos
            </h5>

            {message && <Alert variant="danger">{message}</Alert>}

            <div className="order-section-header" style={{ borderRadius: '14px 14px 0 0', marginTop: 16 }}>
                <i className="fa fa-file-text-o" /> Estado por contrato
            </div>
            {agreements.length === 0 ? (
                <Alert variant="info" className="mt-2">No hay contratos asociados a tu entidad.</Alert>
            ) : (
                <Table responsive className="report-table mb-0">
                    <thead>
                        <tr><th>Contrato</th><th>Cliente</th><th>Estado</th><th>Detalle</th></tr>
                    </thead>
                    <tbody>
                        {agreements.map((a) => (
                            <tr key={a.id}>
                                <td><span className="product-card-extid">{a.ext_id}</span><div style={{ fontSize: 12 }}>{a.description}</div></td>
                                <td style={{ fontSize: 12 }}>{a.customer?.name}</td>
                                <td>{contractState(a)}</td>
                                <td style={{ fontSize: 12 }}>
                                    {a.has_payables ? (
                                        <>
                                            {a.note && <div>{a.note}</div>}
                                            {a.flagged_at && <div style={{ color: 'var(--abi-muted)' }}>Desde {formatDateTime(a.flagged_at)}</div>}
                                        </>
                                    ) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <div className="order-section-header" style={{ borderRadius: '14px 14px 0 0', marginTop: 24 }}>
                <i className="fa fa-history" /> Historial de pagos
            </div>
            {!reports || reports.data.length === 0 ? (
                <Alert variant="info" className="mt-2">Aún no hay pagos registrados.</Alert>
            ) : (
                <>
                    <Table responsive className="report-table mb-0">
                        <thead>
                            <tr><th>Fecha de pago</th><th>Contrato</th><th>Referencia</th><th>Valor</th><th>Origen</th><th>Estado</th><th>Observación</th></tr>
                        </thead>
                        <tbody>
                            {reports.data.map((r) => (
                                <tr key={r.id}>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.paid_at)}</td>
                                    <td style={{ fontSize: 12 }}>{r.agreement?.ext_id}{r.agreement?.customer ? ` — ${r.agreement.customer}` : ''}</td>
                                    <td style={{ fontSize: 12 }}>{r.reference}</td>
                                    <td>{r.amount != null ? currencyFormater(r.amount) : '—'}</td>
                                    <td style={{ fontSize: 12 }}>{SOURCE_LABEL[r.source] || r.source}{r.reported_by ? ` (${r.reported_by})` : ''}</td>
                                    <td><StatusBadge status={r.status} /></td>
                                    <td style={{ fontSize: 12 }}>{r.review_note || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pager page={reports} onChange={load} />
                </>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({ roles: state.roles });
export default connect(mapStateToProps, { setIsLoading })(Payments);
